// Import necessary lists from the utility module
import {
    sellerLists,
    gemtypeLists,
    shapeLists,
    colorLists,
    clarityLists,
    originLists,
    treatmentLists,
    certs
} from '../utils/Utils';

/**
 * Create a paragraph description for a gemstone.
 * @param {Object} gem - The gemstone object containing details.
 * @returns {string} - A descriptive paragraph about the gemstone.
 */
export function CreateParagrahp(gem) {
    const gemType = gemtypeLists.find(item => item.key === gem.type).value;
    const origin = originLists.find(item => item.key === gem.origin).value;
    const color = colorLists.find(item => item.key === gem.color).value;
    const shape = shapeLists.find(item => item.key === gem.shape).value;

    return `This Natural ${gemType} originates from ${origin} and it weighs ${gem.weight} carats. 
            This ${color}, ${shape} shape stone, is size ${gem.width} x ${gem.length} x ${gem.depth} mm. 
            Thanks again! We look forward to serving you.`;
}

/**
 * Generate a random title for a gemstone based on its attributes.
 * @param {Object} gem - The gemstone object containing details.
 * @returns {string} - A randomly generated title for the gemstone.
 */
export function CreateTitle(gem) {
    function randomPrefix() {
        const prefixList = [
            "Ravishing", "Amazing", "Striking", "Gorgeous", "Lovely", "Beautiful",
            "Attractive", "Elegant", "Dazzling", "Twinkling", "Nice", "Marvelous",
            "Stunning", "Wonderful", "Exquisite", "Captivating", "Bewitching"
        ];
        return prefixList[Math.floor(Math.random() * prefixList.length)];
    }
    const certificate = ()=>{
        if (gem.certificate.length>0){
            return gem.certificate[0] + " CERTIFICATE Incl.* "
        } else {
            return ""
        }
    }

    const shape = shapeLists.find(item => item.key === gem.shape).value;
    const color = colorLists.find(item => item.key === gem.color).value;
    const gemType = gemtypeLists.find(item => item.key === gem.type).value;
    const origin = originLists.find(item => item.key === gem.origin).value;
    

    return `${certificate()}${randomPrefix()} ${gem.weight}ct ${shape} ${color} Natural ${gemType} ${origin}`;
}

/**
 * Create the HTML description for eBay listings.
 * @param {Object} gem - The gemstone object containing details.
 * @param {Boolean} track - if True will be add PixelTrack code into HTML Description
 * @returns {string} - The complete HTML description for the eBay listing.
 */
export function CreateEbayDescription(gem, track) {
    const title = gem.title;
    const paragraph = gem.description;
    const PixelTrack = track ? (`<img src="https://app.gemnoble.com:2005/pixel/${gem.id}/" />`) : (``);

    function createImageGallery(gem) {
        if (!gem.images || gem.images.length === 0) return '';
        
        let galleryHtml = '';
        gem.images.forEach((image, index) => {
            galleryHtml += `
                <div class="gallery-item">
                    <img src="${image.source_url}" alt="Gemstone image ${index + 1}" class="gallery-image">
                </div>`;
        });
        
        return `
            <section class="gallery-section">
                <div class="gallery-container">
                    ${galleryHtml}
                </div>
            </section>`;
    }

    function createVideoGallery(gem) {
        if (!gem.videos || gem.videos.length === 0) return '';
        
        let videoGalleryHtml = '';
        gem.videos.forEach((video, index) => {
            videoGalleryHtml += `
                <div class="video-item">
                    <video controls playsinline muted loop class="video-player">
                        <source src="${video.source_url}" type="video/mp4">
                        Your browser does not support the video tag.
                    </video>
                </div>`;
        });
        
        return `
            <section class="video-section">
                <h2 class="section-title">Videos</h2>
                <div class="video-container">
                    ${videoGalleryHtml}
                </div>
            </section>`;
    }

    function createCertificateGallery(gem) {
        if (!gem.certimgs || gem.certimgs.length === 0) return '';
        
        let certGalleryHtml = '';
        gem.certimgs.forEach((cert, index) => {
            certGalleryHtml += `
                <div class="cert-item">
                    <img src="${cert.source_url}" alt="Certificate ${index + 1}" class="cert-image">
                </div>`;
        });
        
        return `
            <section class="cert-section">
                <h2 class="section-title">Certificates</h2>
                <div class="cert-container">
                    ${certGalleryHtml}
                </div>
            </section>`;
    }

    function createSpecifications(gem) {
        const certAttribute = gem.certificate && gem.certificate.length > 0 
            ? `<div class="spec-item"><span class="spec-label">Certificate:</span> ${gem.certificate}</div>` 
            : '';
            
        return `
            <section class="specs-section">
                <h2 class="section-title">Specifications</h2>
                <div class="specs-container">
                    <div class="spec-item"><span class="spec-label">Gem Type:</span> Natural ${gemtypeLists.find(item => item.key === gem.type).value}</div>
                    <div class="spec-item"><span class="spec-label">Weight:</span> ${gem.weight} Carat</div>
                    <div class="spec-item"><span class="spec-label">Shape:</span> ${shapeLists.find(item => item.key === gem.shape).value}</div>
                    <div class="spec-item"><span class="spec-label">Size:</span> ${gem.width} x ${gem.length} x ${gem.depth} mm</div>
                    <div class="spec-item"><span class="spec-label">Color:</span> ${colorLists.find(item => item.key === gem.color).value}</div>
                    <div class="spec-item"><span class="spec-label">Clarity:</span> ${clarityLists.find(item => item.key === gem.clarity).value}</div>
                    <div class="spec-item"><span class="spec-label">Origin:</span> ${originLists.find(item => item.key === gem.origin).value}</div>
                    <div class="spec-item"><span class="spec-label">Treatment:</span> ${treatmentLists.find(item => item.key === gem.treatment).value}</div>
                    ${certAttribute}
                    <div class="spec-item"><span class="spec-label">SKU:</span> ${gem.id}</div>
                </div>
            </section>`;
    }

    function createPolicies() {
        return `
            <section class="policies-section">
                <h2 class="section-title">Policies</h2>
                <div class="policy-container">
                    <div class="policy-item">
                        <h3 class="policy-title">Payment</h3>
                        <p>We accept payments method managed by eBay.</p>
                    </div>
                    
                    <div class="policy-item">
                        <h3 class="policy-title">Shipping</h3>
                        <p>We offer two shipping options:</p>
                        <ul>
                            <li><strong>Economy Shipping:</strong> Provided by THAILAND POST via e-package service. Delivery within 10-20 days.</li>
                            <li><strong>Expedited Shipping:</strong> Provided by FedEx Express. Delivery within 4-7 days.</li>
                        </ul>
                        <p>Free combining for additional items. Maximum 40 items per shipment.</p>
                    </div>
                    
                    <div class="policy-item">
                        <h3 class="policy-title">Returns</h3>
                        <p>Full refund within 14 days from delivery. Items must be returned in original condition.</p>
                        <p>Please <a href="https://www.ebay.com/contact/sendmsg?message_type_id=14&recipient=gemnoble">contact us</a> for return instructions.</p>
                    </div>
                </div>
            </section>`;
    }

    return `<style>
        /* Base Styles */
        :root {
            --primary-color: #000;
            --secondary-color: #333;
            --accent-color: #0071e3;
            --background-color: #fff;
            --light-gray: #f5f5f7;
            --border-color: #d2d2d7;
            --text-color: #1d1d1f;
            --text-secondary: #86868b;
            --spacing-xs: 8px;
            --spacing-sm: 16px;
            --spacing-md: 24px;
            --spacing-lg: 48px;
            --spacing-xl: 64px;
            --border-radius: 12px;
            --transition: all 0.3s ease;
        }
        
        .apple-style * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        .apple-style {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            line-height: 1.5;
            color: var(--text-color);
            background-color: var(--background-color);
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
        }
        
        .apple-style img, 
        .apple-style video {
            max-width: 100%;
            height: auto;
            display: block;
        }
        
        /* Layout */
        .apple-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 var(--spacing-sm);
        }
        
        .apple-section {
            margin-bottom: var(--spacing-lg);
        }
        
        /* Typography */
        .apple-style h1, 
        .apple-style h2, 
        .apple-style h3, 
        .apple-style h4, 
        .apple-style h5, 
        .apple-style h6 {
            font-weight: 600;
            margin-bottom: var(--spacing-sm);
        }
        
        .apple-style h1 {
            font-size: 48px;
            line-height: 1.1;
            letter-spacing: -0.015em;
        }
        
        .apple-style h2.section-title {
            font-size: 32px;
            margin-bottom: var(--spacing-md);
            text-align: center;
        }
        
        .apple-style p {
            margin-bottom: var(--spacing-md);
            font-size: 17px;
        }
        
        /* Header */
        .apple-header {
            text-align: center;
            padding: var(--spacing-xl) 0;
            background-color: var(--light-gray);
            border-radius: var(--border-radius);
            margin-bottom: var(--spacing-lg);
        }
        
        .apple-header h1 {
            max-width: 800px;
            margin: 0 auto var(--spacing-sm);
        }
        
        /* Description */
        .apple-description {
            max-width: 800px;
            margin: 0 auto;
            padding: var(--spacing-lg) var(--spacing-sm);
            text-align: center;
        }
        
        /* Gallery */
        .gallery-section {
            margin: var(--spacing-lg) 0;
        }
        
        .gallery-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: var(--spacing-md);
        }
        
        .gallery-item {
            border-radius: var(--border-radius);
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            transition: var(--transition);
        }
        
        .gallery-item:hover {
            transform: scale(1.02);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }
        
        .gallery-image {
            width: 100%;
            aspect-ratio: 1 / 1;
            object-fit: cover;
        }
        
        /* Video Gallery */
        .video-section {
            margin: var(--spacing-lg) 0;
        }
        
        .video-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: var(--spacing-md);
        }
        
        .video-item {
            border-radius: var(--border-radius);
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
        
        .video-player {
            width: 100%;
            aspect-ratio: 1 / 1;
            object-fit: cover;
        }
        
        /* Certificate Gallery */
        .cert-section {
            margin: var(--spacing-lg) 0;
        }
        
        .cert-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: var(--spacing-md);
        }
        
        .cert-item {
            border-radius: var(--border-radius);
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
        
        .cert-image {
            width: 100%;
            object-fit: contain;
        }
        
        /* Specifications */
        .specs-section {
            background-color: var(--light-gray);
            padding: var(--spacing-lg) 0;
            border-radius: var(--border-radius);
        }
        
        .specs-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: var(--spacing-md);
            max-width: 1000px;
            margin: 0 auto;
        }
        
        .spec-item {
            padding: var(--spacing-sm);
            background-color: var(--background-color);
            border-radius: var(--border-radius);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }
        
        .spec-label {
            font-weight: 600;
            color: var(--text-secondary);
            margin-right: var(--spacing-xs);
        }
        
        /* Policies */
        .policies-section {
            margin: var(--spacing-lg) 0;
        }
        
        .policy-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: var(--spacing-md);
        }
        
        .policy-item {
            padding: var(--spacing-md);
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
        }
        
        .policy-title {
            margin-bottom: var(--spacing-sm);
            color: var(--accent-color);
        }
        
        /* Footer */
        .apple-footer {
            background-color: var(--light-gray);
            padding: var(--spacing-md) 0;
            text-align: center;
            color: var(--text-secondary);
            font-size: 14px;
            border-radius: var(--border-radius);
            margin-top: var(--spacing-lg);
        }
        
        /* Responsive Adjustments */
        @media (max-width: 768px) {
            .apple-style h1 {
                font-size: 36px;
            }
            
            .apple-style h2.section-title {
                font-size: 28px;
            }
            
            .apple-header {
                padding: var(--spacing-lg) 0;
            }
            
            .gallery-container,
            .video-container,
            .cert-container,
            .specs-container,
            .policy-container {
                grid-template-columns: 1fr;
            }
        }
        
        @media (max-width: 480px) {
            .apple-style h1 {
                font-size: 28px;
            }
            
            .apple-style h2.section-title {
                font-size: 24px;
            }
        }
    </style>
    <div class="apple-style">
        <div class="apple-container">
            <header class="apple-header">
                <h1>${title}</h1>
            </header>
            
            <section class="apple-section apple-description">
                <p>${paragraph}</p>
            </section>
            
            ${createImageGallery(gem)}
            
            ${createSpecifications(gem)}
            
            ${createVideoGallery(gem)}
            
            ${createCertificateGallery(gem)}
            
            ${createPolicies()}
            
            <footer class="apple-footer">
                <p>© GemNoble. All rights reserved.</p>
                ${PixelTrack}
            </footer>
        </div>
    </div>`;
}

/**
 * Create a description for WooCommerce listings.
 * @param {Object} gem - The gemstone object containing details.
 * @returns {string} - The complete HTML description for WooCommerce listing.
 */
export function CreateWooDescription(gem) {
    function createCetificateAttributeWoo(gem) {
        return gem.certificate.length <= 0 ? '' : `\n<strong>Certificate: ${gem.certificate} </strong>`;
    }
    return `<p>${gem.description}</p>
        <strong>Gemstone Description:</strong>
        <strong>Type: ${gemtypeLists.find(item => item.key === gem.type).value}</strong>
        <strong>Weight: ${gem.weight}</strong>
        <strong>Shape: ${shapeLists.find(item => item.key === gem.shape).value}</strong>
        <strong>Size: ${gem.width} x ${gem.length} x ${gem.depth}</strong>
        <strong>Color: ${colorLists.find(item => item.key === gem.color).value}</strong>
        <strong>Clarity: ${clarityLists.find(item => item.key === gem.clarity).value}</strong>
        <strong>Origin: ${originLists.find(item => item.key === gem.origin).value}</strong>
        <strong>Treatment: ${treatmentLists.find(item => item.key === gem.treatment).value}</strong>${createCetificateAttributeWoo(gem)}
        <strong>Item Number: ${gem.id}</strong>`;
}

/**
 * Create the XML structure for eBay listing submission.
 * @param {Object} gem - The gemstone object containing details.
 * @param {string} token - The eBay authentication token.
 * @returns {string} - The XML structure for eBay listing submission.
 */
export function createXmlEbayAuction(gem, token) {
    let imgurl = "";
    for (let i = 0; i < gem.images.length; i++) {
        imgurl += `<PictureURL>${gem.images[i].source_url}</PictureURL>\n`;
    }

    return `<?xml version="1.0" encoding="utf-8"?>
        <AddItemRequest xmlns="urn:ebay:apis:eBLBaseComponents">
            <RequesterCredentials>
                <eBayAuthToken>${token}</eBayAuthToken>
            </RequesterCredentials>
            <ErrorLanguage>en_US</ErrorLanguage>
            <WarningLevel>High</WarningLevel>
            <Item>
                <Title>${gem.title || (gem.woo && gem.woo.name) || 'Untitled Gem'}</Title>
                <Description><![CDATA[${CreateEbayDescription(gem,true)}]]></Description>
                <PrimaryCategory>
                    <CategoryID>262027</CategoryID>
                </PrimaryCategory>
                <StartPrice>${gem.price}</StartPrice>
                <CategoryMappingAllowed>true</CategoryMappingAllowed>
                <ConditionID>1500</ConditionID>
                <Country>TH</Country>
                <Currency>USD</Currency>
                <DispatchTimeMax>3</DispatchTimeMax>
                <ListingDuration>Days_7</ListingDuration>
                <ListingType>Chinese</ListingType>
                <Location>Thailand</Location>
                <PictureDetails>${imgurl}</PictureDetails>
                <PostalCode>22000</PostalCode>
                <Quantity>1</Quantity>
                <ItemSpecifics>
                    <NameValueList>
                        <Name>Gemstone Type</Name>
                        <Value>${gemtypeLists.find(item => item.key === gem.type).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Total Carat Weight (TCW)</Name>
                        <Value>${gem.weight}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Gemstone Shape</Name>
                        <Value>${shapeLists.find(item => item.key === gem.shape).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Gemstone Color</Name>
                        <Value>${colorLists.find(item => item.key === gem.color).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Country of Origin</Name>
                        <Value>${originLists.find(item => item.key === gem.origin).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Brand</Name>
                        <Value>Gemnoble</Value>
                    </NameValueList>
                </ItemSpecifics>
                <Quantity>1</Quantity>
                <SellerProfiles>
      		        <SellerShippingProfile>
       			        <ShippingProfileID>305171173023</ShippingProfileID>
    		  	    </SellerShippingProfile>
      		        <SellerReturnProfile>
        			    <ReturnProfileID>298814943023</ReturnProfileID>
      		        </SellerReturnProfile>
      		        <SellerPaymentProfile>
        			    <PaymentProfileID>298814233023</PaymentProfileID>
      		    </SellerPaymentProfile>
                </SellerProfiles>
                <Site>US</Site>
            </Item>
        </AddItemRequest>`;
}

/**
 * Create the XML structure for eBay listing submission.
 * @param {Object} gem - The gemstone object containing details.
 * @param {string} token - The eBay authentication token.
 * @returns {string} - The XML structure for eBay listing submission.
 */
export function createXmlEbayFixed(gem, token) {
    let imgurl = "";
    for (let i = 0; i < gem.images.length; i++) {
        imgurl += `<PictureURL>${gem.images[i].source_url}</PictureURL>\n`;
    }

    return `<?xml version="1.0" encoding="utf-8"?>
            <AddFixedPriceItemRequest xmlns="urn:ebay:apis:eBLBaseComponents">
                <ErrorLanguage>en_US</ErrorLanguage>
                <WarningLevel>High</WarningLevel>
                <Item>
                    <Title>${gem.title || (gem.woo && gem.woo.name) || 'Untitled Gem'}</Title>
                    <Description><![CDATA[${CreateEbayDescription(gem,true)}]]></Description>
                    <PrimaryCategory>
                    <CategoryID>262027</CategoryID>
                    </PrimaryCategory>
                    <StartPrice>${gem.price}</StartPrice>
                    <ConditionID>1500</ConditionID>
                    <Country>TH</Country>
                    <Currency>USD</Currency>
                    <DispatchTimeMax>3</DispatchTimeMax>
                    <ListingDuration>GTC</ListingDuration>
                    <ListingType>FixedPriceItem</ListingType>
                    <Location>Thailand</Location>
                    <PictureDetails>${imgurl}</PictureDetails>
                    <PostalCode>22000</PostalCode>
                    <ProductListingDetails>
                    <UPC></UPC>
                    <IncludeStockPhotoURL>true</IncludeStockPhotoURL>
                    <IncludeeBayProductDetails>true</IncludeeBayProductDetails>
                    <UseFirstProduct>true</UseFirstProduct>
                    <ReturnSearchResultOnDuplicates>true</ReturnSearchResultOnDuplicates>
                    </ProductListingDetails>
                    <ItemSpecifics>
                    <NameValueList>
                        <Name>Gemstone Type</Name>
                        <Value>${gemtypeLists.find(item => item.key === gem.type).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Total Carat Weight (TCW)</Name>
                        <Value>${gem.weight}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Gemstone Shape</Name>
                        <Value>${shapeLists.find(item => item.key === gem.shape).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Gemstone Color</Name>
                        <Value>${colorLists.find(item => item.key === gem.color).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Country of Origin</Name>
                        <Value>${originLists.find(item => item.key === gem.origin).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Brand</Name>
                        <Value>Gemnoble</Value>
                    </NameValueList>
                </ItemSpecifics>
                    <Quantity>1</Quantity>
                    <SellerProfiles>
      		        <SellerShippingProfile>
       			        <ShippingProfileID>305171173023</ShippingProfileID>
    		  	    </SellerShippingProfile>
      		        <SellerReturnProfile>
        			    <ReturnProfileID>298814943023</ReturnProfileID>
      		        </SellerReturnProfile>
      		        <SellerPaymentProfile>
        			    <PaymentProfileID>298814233023</PaymentProfileID>
      		    </SellerPaymentProfile>
                </SellerProfiles>
                    <Site>US</Site>
            </Item>
            </AddFixedPriceItemRequest>
`;
}

/**
 * Create the XML structure for eBay listing submission.
 * @param {Object} gem - The gemstone object containing details.
 * @param {string} token - The eBay authentication token.
 * @returns {string} - The XML structure for eBay listing submission.
 */
export function createXmlEbay(gem, token) {
    let imgurl = "";
    for (let i = 0; i < gem.images.length; i++) {
        imgurl += `<PictureURL>${gem.images[i].source_url}</PictureURL>\n`;
    }

    return `<?xml version="1.0" encoding="utf-8"?>
        <AddItemRequest xmlns="urn:ebay:apis:eBLBaseComponents">
            <RequesterCredentials>
                <eBayAuthToken>${token}</eBayAuthToken>
            </RequesterCredentials>
            <ErrorLanguage>en_US</ErrorLanguage>
            <WarningLevel>High</WarningLevel>
            <Item>
                <Title>${gem.title || (gem.woo && gem.woo.name) || 'Untitled Gem'}</Title>
                <Description><![CDATA[${CreateEbayDescription(gem,true)}]]></Description>
                <PrimaryCategory>
                    <CategoryID>262027</CategoryID>
                </PrimaryCategory>
                <StartPrice>${gem.price}</StartPrice>
                <CategoryMappingAllowed>true</CategoryMappingAllowed>
                <ConditionID>1500</ConditionID>
                <Country>TH</Country>
                <Currency>USD</Currency>
                <DispatchTimeMax>3</DispatchTimeMax>
                <ListingDuration>Days_7</ListingDuration>
                <ListingType>Chinese</ListingType>
                <Location>Thailand</Location>
                <PictureDetails>${imgurl}</PictureDetails>
                <PostalCode>22000</PostalCode>
                <Quantity>1</Quantity>
                <ItemSpecifics>
                    <NameValueList>
                        <Name>Gemstone Type</Name>
                        <Value>${gemtypeLists.find(item => item.key === gem.type).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Total Carat Weight (TCW)</Name>
                        <Value>${gem.weight}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Gemstone Shape</Name>
                        <Value>${shapeLists.find(item => item.key === gem.shape).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Gemstone Color</Name>
                        <Value>${colorLists.find(item => item.key === gem.color).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Country of Origin</Name>
                        <Value>${originLists.find(item => item.key === gem.origin).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Brand</Name>
                        <Value>Gemnoble</Value>
                    </NameValueList>
                </ItemSpecifics>
                <Quantity>1</Quantity>
                <SellerProfiles>
      		        <SellerShippingProfile>
       			        <ShippingProfileID>305171173023</ShippingProfileID>
    		  	    </SellerShippingProfile>
      		        <SellerReturnProfile>
        			    <ReturnProfileID>298814943023</ReturnProfileID>
      		        </SellerReturnProfile>
      		        <SellerPaymentProfile>
        			    <PaymentProfileID>298814233023</PaymentProfileID>
      		    </SellerPaymentProfile>
                </SellerProfiles>
                <Site>US</Site>
            </Item>
        </AddItemRequest>`;
}

/**
 * Create the XML structure for eBay listing submission.
 * @param {Object} gem - The gemstone object containing details.
 * @returns {string} - The XML Item for create xml structure for eBay listing submission.
 */
export function createXmlItem(gem) {
    let imgurl = "";
    for (let i = 0; i < gem.images.length; i++) {
        imgurl += `<PictureURL>${gem.images[i].source_url}</PictureURL>\n`;
    }

    return `
            <Item>
                <Title>${gem.title}</Title>
                <Description><![CDATA[${CreateEbayDescription(gem,true)}]]></Description>
                <PrimaryCategory>
                    <CategoryID>262027</CategoryID>
                </PrimaryCategory>
                <StartPrice>${gem.price}</StartPrice>
                <CategoryMappingAllowed>true</CategoryMappingAllowed>
                <ConditionID>1500</ConditionID>
                <Country>TH</Country>
                <Currency>USD</Currency>
                <DispatchTimeMax>3</DispatchTimeMax>
                <ListingDuration>Days_7</ListingDuration>
                <ListingType>Chinese</ListingType>
                <Location>Thailand</Location>
                <PictureDetails>${imgurl}</PictureDetails>
                <PostalCode>22000</PostalCode>
                <Quantity>1</Quantity>
                <ItemSpecifics>
                    <NameValueList>
                        <Name>Gemstone Type</Name>
                        <Value>${gemtypeLists.find(item => item.key === gem.type).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Total Carat Weight (TCW)</Name>
                        <Value>${gem.weight}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Gemstone Shape</Name>
                        <Value>${shapeLists.find(item => item.key === gem.shape).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Gemstone Color</Name>
                        <Value>${colorLists.find(item => item.key === gem.color).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Country of Origin</Name>
                        <Value>${originLists.find(item => item.key === gem.origin).value}</Value>
                    </NameValueList>
                    <NameValueList>
                        <Name>Brand</Name>
                        <Value>Gemnoble</Value>
                    </NameValueList>
                </ItemSpecifics>
                <Quantity>1</Quantity>
                <SellerProfiles>
      		        <SellerShippingProfile>
       			        <ShippingProfileID>305171173023</ShippingProfileID>
    		  	    </SellerShippingProfile>
      		        <SellerReturnProfile>
        			    <ReturnProfileID>298814943023</ReturnProfileID>
      		        </SellerReturnProfile>
      		        <SellerPaymentProfile>
        			    <PaymentProfileID>298814233023</PaymentProfileID>
      		    </SellerPaymentProfile>
                </SellerProfiles>
                <Site>US</Site>
            </Item>
        `;
}

/**
 * Set price options for the item in WooCommerce.
 * @param {string} priceOption - The selected pricing option ('open' or 'sale').
 * @returns {Object} - The corresponding configuration object.
 */
export function PriceOption(priceOption) {
    if (priceOption === 'open') {
        return {
            key: "offers_for_woocommerce_enabled",
            value: "yes"
        };
    } else if (priceOption === 'sale') {
        return {
            key: "offers_for_woocommerce_enabled",
            value: "no"
        };
    }
}
